import { Fragment, useState, type JSX, type ReactNode } from 'react';
import { useTranslation } from '../i18n';
import { Icon } from './Icon';
import { sortRows, toggleSort, type SortState, type SortValue } from './sorting';
import { useIsPhone } from './viewport';

export interface Column<T> {
  key: string;
  header: string;
  /**
   * Доля ширины в grid-template-columns, например '1.4fr' или '96px'.
   *
   * Доли (`fr`) сжимаются до нуля и обрезают содержимое многоточием;
   * пиксели — нет: содержимое шире заданного вылезает из ячейки и даёт
   * горизонтальную прокрутку у всей страницы. Поэтому колонку с рядом
   * кнопок считают, а не прикидывают:
   *
   * - иконочная `sm`-кнопка — **36 px** (`px-2.5` по краям плюс глиф 16);
   * - зазор `gap-1` между ними — **4 px**.
   *
   * Считать это руками не нужно: для ряда иконочных кнопок есть
   * `actionsWidth(сколько)`.
   *
   * `max-content` здесь **не работает**: шапка и каждая строка — отдельные
   * гриды с общим шаблоном, и авторазмер дал бы каждой свою ширину колонки.
   */
  width: string;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right';
  /**
   * Значение для сортировки. Задано — колонка сортируется щелчком
   * по заголовку.
   *
   * Отдельно от `render`, потому что отрисованное и сравниваемое —
   * разные вещи: в ячейке «12.5 мс» или значок состояния, а сравнивать
   * надо число и признак. Вытаскивать значение из разметки значило бы
   * сортировать по тексту, включая единицы измерения.
   */
  sortValue?: (row: T) => SortValue;
  /**
   * Чем колонка становится на телефоне, где строка превращается в карточку.
   *
   * Не задано — считается по порядку колонок (см. `ColumnPriority`).
   */
  priority?: ColumnPriority;
}

/** Ширина иконочной кнопки `size="sm"`: `px-2.5` по краям плюс глиф 16 px. */
const ICON_BUTTON_PX = 36;

/** Зазор `gap-1` между кнопками в ряду. */
const BUTTON_GAP_PX = 4;

/**
 * Ширина колонки под ряд иконочных кнопок.
 *
 * Функция, а не число в каждой таблице: пиксельная колонка ни сжимается,
 * ни растёт, и ряд кнопок, не поместившийся в неё, вылезает наружу
 * и тянет за собой горизонтальную прокрутку **всей страницы**. Заметно это
 * не там, где ошиблись, а внизу экрана — и уже дважды замечалось не сразу:
 * сперва у агентов, потом у пользователей, когда к кнопкам добавили правку
 * и сброс пароля.
 *
 * Теперь добавление кнопки — это изменение числа здесь, а не арифметика
 * в уме.
 */
export function actionsWidth(buttons: number): string {
  const count = Math.max(1, buttons);

  return `${count * ICON_BUTTON_PX + (count - 1) * BUTTON_GAP_PX}px`;
}

/**
 * Роль колонки на телефоне.
 *
 * Строка в шесть колонок на 360 px не помещается никак — не из-за длинных
 * текстов, а из-за пиксельных колонок, которые не сжимаются вовсе: у самой
 * тяжёлой таблицы платформы их на 634 px. Поэтому на телефоне строка
 * становится карточкой, и порядок колонок сменяется важностью.
 *
 * - `mark` — узкий индикатор слева от имени: точка состояния, галочка;
 * - `title` — то, по чему строку узнают: имя узла, площадки, канала;
 * - `summary` — то, что смотрят вместе с именем: адрес, состояние, время;
 * - `actions` — кнопки строки, справа от имени;
 * - `detail` — остальное, по нажатию.
 *
 * Не задано — считается по порядку: первая узкая пиксельная колонка это
 * `mark`, первая обычная — `title`, следующие две — `summary`, колонка
 * с ключом `actions` — кнопки, остальное — `detail`. Умолчание выбрано так,
 * чтобы таблица, которую никто не размечал, всё равно давала осмысленную
 * карточку: разметка уточняет, а не включает.
 */
export type ColumnPriority = 'mark' | 'title' | 'summary' | 'actions' | 'detail';

/** Ширина, ниже которой пиксельная колонка считается индикатором, а не данными. */
const MARK_WIDTH_PX = 32;

/** Сколько колонок уходит во вторую строку карточки, когда никто не разметил. */
const DEFAULT_SUMMARY = 2;

interface Roles<T> {
  mark: Column<T> | null;
  title: Column<T> | null;
  summary: Column<T>[];
  actions: Column<T> | null;
  detail: Column<T>[];
}

export function columnRoles<T>(columns: readonly Column<T>[]): Roles<T> {
  const roles: Roles<T> = { mark: null, title: null, summary: [], actions: null, detail: [] };
  const rest: { column: Column<T>; index: number }[] = [];

  // Сначала размеченное: иначе первая неразмеченная колонка успела бы занять
  // место заголовка, и явное `priority: 'title'` ниже по списку не сработало
  // бы вовсе. Ровно этот случай — журнал аудита, где первая колонка время,
  // а заголовком должно быть действие.
  columns.forEach((column, index) => {
    switch (column.priority) {
      case 'mark':
        roles.mark ??= column;
        break;
      case 'title':
        roles.title ??= column;
        break;
      case 'summary':
        roles.summary.push(column);
        break;
      case 'actions':
        roles.actions ??= column;
        break;
      case 'detail':
        roles.detail.push(column);
        break;
      default:
        rest.push({ column, index });
    }
  });

  for (const { column, index } of rest) {
    const pixels = column.width.endsWith('px') ? Number.parseInt(column.width, 10) : null;

    // Индикатор — только первая колонка: узкая пиксельная колонка в середине
    // таблицы это данные (порт, число узлов), а не состояние строки.
    if (roles.mark === null && index === 0 && pixels !== null && pixels <= MARK_WIDTH_PX) {
      roles.mark = column;
      continue;
    }

    if (column.key === 'actions') {
      roles.actions ??= column;
      continue;
    }

    if (roles.title === null) {
      roles.title = column;
      continue;
    }

    if (roles.summary.length < DEFAULT_SUMMARY) {
      roles.summary.push(column);
      continue;
    }

    roles.detail.push(column);
  }

  return roles;
}

/**
 * Список карточек вместо таблицы.
 *
 * Карточка — это не «таблица в столбик»: подписи колонок в ней появляются
 * только у раскрытых подробностей. Имя и два значения рядом с ним читаются
 * и без подписей, а «Площадка: Склад» в каждой из сорока карточек — это
 * сорок лишних слов на экране, где места и так нет.
 */
function CardList<T>({
  columns,
  rows,
  rowKey,
  rowDanger,
  onRowClick,
  selectedKey,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  rowDanger?: ((row: T) => boolean) | undefined;
  onRowClick?: ((row: T) => void) | undefined;
  selectedKey?: string | undefined;
}): JSX.Element {
  const { t } = useTranslation();

  const roles = columnRoles(columns);
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());

  const toggle = (key: string): void =>
    setOpened((current) => {
      const next = new Set(current);

      if (!next.delete(key)) {
        next.add(key);
      }

      return next;
    });

  return (
    <ul className="flex flex-col">
      {rows.map((row) => {
        const key = rowKey(row);
        const open = opened.has(key);
        const selected = selectedKey !== undefined && key === selectedKey;

        return (
          <li
            key={key}
            className={[
              'flex flex-col gap-1 border-b border-row-line px-3 py-2.5 text-base',
              rowDanger?.(row) ? 'bg-down-bg' : '',
              selected ? 'bg-overlay shadow-[inset_2px_0_0_var(--nd-text)]' : '',
            ].join(' ')}
          >
            <div className="flex min-h-9 items-center gap-2">
              {roles.mark ? <span className="flex-none">{roles.mark.render(row)}</span> : null}

              {/*
                Нажатие по карточке делает то же, что нажатие по строке
                на десктопе. Если строка ничего не делает — раскрывает
                подробности: иначе к ним не добраться вовсе.
              */}
              <button
                type="button"
                onClick={() => (onRowClick ? onRowClick(row) : toggle(key))}
                aria-expanded={onRowClick ? undefined : open}
                className="min-w-0 flex-1 truncate text-left"
              >
                {roles.title ? roles.title.render(row) : key}
              </button>

              {roles.actions ? (
                <span className="flex-none">{roles.actions.render(row)}</span>
              ) : null}

              {roles.detail.length > 0 && onRowClick ? (
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  aria-expanded={open}
                  aria-label={
                    open ? t('common.action.hideDetails') : t('common.action.showDetails')
                  }
                  className="flex size-9 flex-none items-center justify-center text-dim"
                >
                  <span className={`transition-transform ${open ? 'rotate-180' : ''}`}>
                    <Icon name="chevronDown" />
                  </span>
                </button>
              ) : null}
            </div>

            {roles.summary.length > 0 ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted">
                {roles.summary.map((column) => (
                  <span key={column.key} className="min-w-0 max-w-full truncate">
                    {column.render(row)}
                  </span>
                ))}
              </div>
            ) : null}

            {open && roles.detail.length > 0 ? (
              <dl className="grid grid-cols-[minmax(0,96px)_minmax(0,1fr)] gap-x-3 gap-y-1 pt-1 text-sm">
                {roles.detail.map((column) => (
                  <Fragment key={column.key}>
                    <dt className="truncate font-mono text-2xs text-faint uppercase">
                      {column.header || '—'}
                    </dt>
                    <dd className="min-w-0">{column.render(row)}</dd>
                  </Fragment>
                ))}
              </dl>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Подсветить строку как аварийную — фон и рамка из палитры down. */
  rowDanger?: (row: T) => boolean;
  /** Выбор строки. Делает строки интерактивными и доступными с клавиатуры. */
  onRowClick?: (row: T) => void;
  /** Ключ выбранной строки — подсвечивается как активная. */
  selectedKey?: string | undefined;
  /** Колонка и направление, с которых таблица открывается. */
  defaultSort?: SortState;
  empty?: ReactNode;
  /**
   * Сколько строк видно без прокрутки; остальные — под полосой внутри таблицы.
   *
   * Для таблиц, которые стоят среди других блоков и не должны растягивать
   * страницу: лента событий на обзоре. На телефоне не действует — вложенная
   * прокрутка там перехватывает жест, которым листают страницу.
   */
  maxRows?: number;
}

/** Высота заголовка и строки — те же 30 и 34 px, что в разметке ниже. */
const HEADER_HEIGHT_PX = 30;
const ROW_HEIGHT_PX = 34;

/**
 * Плотная таблица по макету, раздел «04».
 *
 * Высота строки 34px, заголовка — 30px: §12 требует плотности выше
 * воздушности, чтобы на экран помещалось 40 узлов, а не 8 карточек.
 * Разметка на grid, а не на table: колонки должны делить ширину долями,
 * и при этом строка целиком остаётся кликабельной областью.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowDanger,
  onRowClick,
  selectedKey,
  defaultSort,
  empty,
  maxRows,
}: DataTableProps<T>): JSX.Element {
  const { t } = useTranslation();

  /*
    Доли оборачиваются в `minmax(0, …)`. Без этого `1fr` означает
    `minmax(auto, 1fr)`: колонка не сжимается ниже содержимого, длинный
    адрес или ряд кнопок распирают таблицу шире экрана, и внизу появляется
    горизонтальная прокрутка. С нулевым минимумом колонка ужимается,
    а содержимое обрезается многоточием — как и задумано.
  */
  const template = columns
    .map((column) => (column.width.endsWith('fr') ? `minmax(0, ${column.width})` : column.width))
    .join(' ');

  /*
    Сортировка живёт в самой таблице, а не поднимается к вызывающему.
    Это оформление списка, а не его состав: тот, кто передал строки, отвечает
    за то, какие они, а как их разложить перед глазами — дело таблицы.
    Поднятая наверх, она потребовала бы состояния и обработчика на каждом
    экране со списком, и на восьми экранах это восемь одинаковых копий.
  */
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);

  const phone = useIsPhone();

  const sortable = new Map(
    columns.filter((column) => column.sortValue).map((column) => [column.key, column.sortValue!]),
  );

  const ordered =
    sort && sortable.has(sort.key)
      ? sortRows(rows, sort, (row, key) => sortable.get(key)?.(row))
      : rows;

  if (rows.length === 0 && empty) {
    return <>{empty}</>;
  }

  /*
    На телефоне таблицы нет. Дело не в тесноте: пиксельные колонки
    не сжимаются, и строка не может стать у́же суммы своих — у «Активных
    сессий» это 634 px при экране в 360. Единственное, что оставалось
    раньше, — возить таблицу вбок внутри панели.
  */
  if (phone) {
    return (
      <CardList
        columns={columns}
        rows={ordered}
        rowKey={rowKey}
        rowDanger={rowDanger}
        onRowClick={onRowClick}
        selectedKey={selectedKey}
      />
    );
  }

  return (
    <div
      role="table"
      className={`min-w-full ${maxRows === undefined ? '' : 'overflow-y-auto'}`}
      style={
        maxRows === undefined
          ? undefined
          : { maxHeight: `${String(HEADER_HEIGHT_PX + maxRows * ROW_HEIGHT_PX)}px` }
      }
    >
      <div
        role="row"
        // Шапка закреплена: в прокручиваемой таблице без неё непонятно,
        // какая колонка время, а какая площадка.
        className={`grid h-[30px] items-center gap-3 border-b border-line bg-row-alt px-3.5 font-mono text-2xs tracking-[0.06em] text-dim uppercase ${
          maxRows === undefined ? '' : 'sticky top-0 z-[1]'
        }`}
        style={{ gridTemplateColumns: template }}
      >
        {columns.map((column) => {
          const active = sort?.key === column.key ? sort.direction : null;
          const alignment = column.align === 'right' ? 'justify-end text-right' : '';

          return (
            <span
              role="columnheader"
              key={column.key}
              aria-sort={active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : 'none'}
              className={column.align === 'right' ? 'text-right' : undefined}
            >
              {column.sortValue ? (
                <button
                  type="button"
                  onClick={() => setSort((current) => toggleSort(current, column.key))}
                  // Заголовок без подписи сортировать можно, но нажимать
                  // не на что: имя для скринридера берётся из ключа.
                  aria-label={t('common.action.sortBy', { column: column.header || column.key })}
                  className={`flex w-full items-center gap-1 uppercase hover:text-fg ${alignment} ${
                    active ? 'text-fg' : ''
                  }`}
                >
                  {column.header}
                  <span aria-hidden="true" className={active ? '' : 'text-faint'}>
                    {active === 'asc' ? '↑' : active === 'desc' ? '↓' : '↕'}
                  </span>
                </button>
              ) : (
                column.header
              )}
            </span>
          );
        })}
      </div>

      {ordered.map((row) => {
        const key = rowKey(row);
        const selected = selectedKey !== undefined && key === selectedKey;

        return (
          <div
            role="row"
            key={key}
            // Строка выбирается и мышью, и с клавиатуры: §12 требует плотной
            // клавиатурной навигации, а таблица — основной способ работы здесь.
            {...(onRowClick
              ? {
                  tabIndex: 0,
                  'aria-selected': selected,
                  onClick: () => onRowClick(row),
                  onKeyDown: (event: React.KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowClick(row);
                    }
                  },
                }
              : {})}
            className={[
              'grid min-h-[34px] items-center gap-3 border-b border-row-line px-3.5 text-sm',
              rowDanger?.(row) ? 'bg-down-bg' : '',
              onRowClick ? 'cursor-pointer hover:bg-row-alt' : '',
              selected ? 'bg-overlay shadow-[inset_2px_0_0_var(--nd-text)]' : '',
            ].join(' ')}
            style={{ gridTemplateColumns: template }}
          >
            {columns.map((column) => (
              <span
                role="cell"
                key={column.key}
                // `min-w-0` нужен и правой колонке: без него содержимое
                // ячейки задаёт ей минимальную ширину, и сжать её нельзя.
                className={`min-w-0 ${column.align === 'right' ? 'text-right' : ''}`}
              >
                {column.render(row)}
              </span>
            ))}
          </div>
        );
      })}
    </div>
  );
}
